import * as Combokeys from "combokeys";
import * as React from "react";
import { clearDragState, getDragState, getDayViewGrabOffsetMinutes, isDropOperationPending, isOpenShiftAssignPending, isTargetOpenShiftUpdatePending, setDayViewGrabOffsetMinutes, setDragState, setHadValidHover, setOverrideDropStartTime, DND_PADDING_TOP, DND_PADDING_LEFT_RIGHT, DND_PADDING_BOTTOM } from "sh-application/components/schedule/dragDrop/shiftDragState";
import ActivityFlexContainer from "../../activity/ActivityFlexContainer";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import AvailabilityUtils from "sh-application/utility/AvailabilityUtils";
import ConflictDetailsCallout from "sh-application/components/shift/conflictDetails/ConflictDetailsCallout";
import ConflictUtils from "sh-application/utility/ConflictUtils";
import EditStatusIndicator from "sh-application/components/common/EditStatusIndicator";
import EmptyCellAvailability from "./EmptyCellAvailability";
import IBaseScheduleCellProps from "./IBaseScheduleCellProps";
import InstrumentationUtils from "sh-application/utility/InstrumentationUtils";
import MemberUtils from "sh-application/utility/MemberUtils";
import OpenShiftRequestApprovalCallout from "sh-application/components/shiftrequest/approvalCallout/OpenShiftRequestApprovalCallout";
import PrintComponent from "sh-application/components/common/PrintComponent";
import schedulesViewStateStore from "sh-application/components/schedules/lib/store/store";
import Shift from "../../shift/Shift";
import ShiftContextualMenu from "../../shift/ShiftContextualMenu";
import ShiftDetailsCallout from "../../shift/shiftDetails/ShiftDetailsCallout";
import ShiftRequestsIndicator from "sh-application/components/shift/shiftRequestsIndicator/ShiftRequestsIndicator";
import ShiftRequestUtils from "sh-application/utility/ShiftRequestUtils";
import ShiftThemeContainer from "../../shift/ShiftThemeContainer";
import ShiftUtils from "sh-application/utility/ShiftUtils";
import StringsStore from "sh-strings/store";
import TimeOffRequestApprovalCallout from "sh-application/components/shift/TimeOffRequestApprovalCallout";
import TimeOffRequestShift from "../../shift/TimeOffRequestShift";
import TimeOffShift from "../../shift/TimeOffShift";
import { calculateRequestConflicts } from "sh-requestconflict-store";
import { default as KeyboardUtils } from "sh-application/utility/KeyboardUtils";
import {
    ECSConfigKey,
    ECSConfigService,
    FlightSettingsService,
    InstrumentationService
    } from "sh-services";
import {
    FlightKeys,
    IBaseConflictEntity,
    IBaseShiftEntity,
    IGroupedOpenShiftRequestEntity,
    IMemberEntity,
    IOpenShiftEntity,
    IShiftEntity,
    IUniqueShiftEntity,
    ScheduleCalendarType,
    ScheduleCalendarTypes,
    ShiftRequestStates
    } from "sh-models";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { IButton, IconButton } from "@fluentui/react";
import { Moment } from "moment";
import { ScheduleCellRenderSize } from "sh-application/../StaffHubConstants";
import { ScheduleGridCssClasses, ScheduleGridUtils } from "sh-application/components/schedules/lib";
import { SlotBarProps } from "sh-application/components/shift/SlotBar";
import { StaffHubTourMarker } from "sh-application/components/tour/sh-tour/StaffHubTourMarker";
import { TeamStore } from "sh-stores/sh-team-store";

const classNames = require("classnames/bind");
const styles = require("./ScheduleCell.scss");

interface PrintBackgroundProps {
    shift: IBaseShiftEntity;
}

const PrintBackground = function(props: PrintBackgroundProps) {
    const { shift } = props;
    if (!shift) {
        return null;
    }
    const theme = ShiftUtils.getShiftThemeClass(shift);
    return (
        <span className={ styles.printBackgroundContainer }>
            <span className={ classNames(styles.printBackground, styles[theme]) }></span>
        </span>
    );
};

interface ScheduleCellState {
    isMoreMenuCalloutVisible: boolean;              // true when the ... (more menu) is open
    isShiftLookupCalloutVisible: boolean;           // true when the shift lookup menu is open
    isOpenShiftRequestCalloutVisible: boolean;      // true when open shift request approve/deny callout is open
    instrumentationAction: string;                  // action to log in instrumentation
    triggerKey: string;                             // the key that opened the last callout
    isConflictCalloutVisible: boolean;              // true when the conflict callout is open
    renderExtras: boolean;
    isDragOver: boolean;                            // true while a dragged shift is hovering over this cell
    isDragging: boolean;                            // true while this cell's shift is being dragged
}

interface MultiDayDropPreview {
    targetStartTime: Moment;
    left: number;
    width: number;
}

const MULTI_DAY_DROP_PREVIEW_ID = "multi-day-shift-drop-preview";

function showMultiDayDropPreview(ownerId: string, preview: MultiDayDropPreview, cellRect: DOMRect): void {
    let previewElement = document.getElementById(MULTI_DAY_DROP_PREVIEW_ID);
    if (!previewElement) {
        previewElement = document.createElement("div");
        previewElement.id = MULTI_DAY_DROP_PREVIEW_ID;
        previewElement.className = styles.multiDayDropPreview;
        document.body.appendChild(previewElement);
    }

    previewElement.dataset.ownerId = ownerId;
    previewElement.style.left = `${preview.left}px`;
    previewElement.style.top = `${cellRect.top}px`;
    previewElement.style.width = `${preview.width}px`;
    previewElement.style.height = `${cellRect.height}px`;
}

function clearMultiDayDropPreview(ownerId?: string): void {
    const previewElement = document.getElementById(MULTI_DAY_DROP_PREVIEW_ID);
    if (previewElement && (!ownerId || previewElement.dataset.ownerId === ownerId)) {
        previewElement.remove();
    }
}

/**
 * Callbacks for operations that can be performed for a schedule cell (eg, copy + paste, delete, etc)
 */
export interface ScheduleCellActionCallbacks {
    cellCopyCallback: ((shift?: IBaseShiftEntity) => void) | null;      // Copy the cell
    cellPasteCallback: ((targetCellIndex?: number) => void) | null;     // Paste onto the cell
    cellShareCallback: ((shift: IBaseShiftEntity) => void) | null;     // Share the shift in the cell
    cellAssignOpenShiftCallback: ((openShift: IOpenShiftEntity, member: IMemberEntity) => void); // assign the open shift in the cell to a member
    cellMoveToOpenShiftsCallback: ((shift: IBaseShiftEntity, shiftMember: IMemberEntity, targetDate: Moment, targetGroupTagId: string) => void); // move the shift to open shifts
    canPerformPasteCallback: (() => boolean);   // Determine if a paste can be performed
    cellDeleteCallback: ((shift: IBaseShiftEntity) => void) | null;    // Delete the cell's contents
    applyUniqueShiftCallback: ApplyUniqueShiftCallbackFunction;
}

export type ApplyUniqueShiftCallbackFunction =  ((                // Apply a unique shift to the selection
    uniqueShift: IUniqueShiftEntity,
    tenantId: string,
    teamId: string) => void) | null;

const SCHEDULE_CELL_BASE_CSSCLASS = "scheduleCell";

/**
 * ScheduleCell component
 * This renders the contents of a schedule cell (eg, shift, timeoff)
 */
class ScheduleCell extends React.Component<IBaseScheduleCellProps, ScheduleCellState> {
    private static _nextDropPreviewOwnerId = 0;
    // Unique per-instance owner id for the shared multi-day drop preview element.
    // dataSelectionIndexVal can be undefined (and is not guaranteed unique), so an
    // instance-scoped id ensures one cell's onDragLeave never clears a preview that a
    // different cell just showed.
    private readonly _dropPreviewOwnerId = `cell-${ScheduleCell._nextDropPreviewOwnerId++}`;
    private _cellElement = React.createRef<HTMLDivElement>();
    private _shiftLookupElement = React.createRef<IButton>();

    private _combokeys: Combokeys.Combokeys;
    // Native capture-phase dragstart listener — catches drags that bypass React's
    // document-level synthetic event delegation (e.g. child SVG/image native drags).
    private _nativeDragStartHandler: ((e: DragEvent) => void) | null = null;
    // True when the last mousedown landed on the cell's padding area (not on the shift card itself).
    // Used to cancel drag and allow multi-select when dragging-and-drop whitespace is enabled.
    private _paddingMouseDown = false;
    private static _containerClasses = classNames(SCHEDULE_CELL_BASE_CSSCLASS, styles.scheduleCell);
    private static _emptyCellContainerClasses = "";
    private static _emptyCellOpenShiftContainerClasses = "";
    private static _lastCalendarType: ScheduleCalendarType = null;
    private static _contentClasses = classNames(styles.innerContainer, "shiftContent");
    private static _contextMenuButtonAriaLabel = "";
    private static _contextMenuButtonTooltip = "";
    private static _shiftLookupContextMenuButtonAriaLabel = "";
    private static _shiftLookupContextMenuButtonTooltip = "";

    constructor(props: IBaseScheduleCellProps) {
        super(props);

        if (!ScheduleCell._lastCalendarType || (ScheduleCell._lastCalendarType !== props.scheduleCalendarType)) {
            // set the last calendar type so we can keep track of when it changes
            ScheduleCell._lastCalendarType = props.scheduleCalendarType;
            // if the calendar type is changing, clear the calculated empty cell container classes
            ScheduleCell._emptyCellContainerClasses = null;
            ScheduleCell._emptyCellOpenShiftContainerClasses = null;
        }

        if (!ScheduleCell._contextMenuButtonAriaLabel) {
            ScheduleCell._contextMenuButtonAriaLabel = StringsStore().registeredStringModules.get("common").strings.get("openContextMenuButtonAriaLabel");
            ScheduleCell._contextMenuButtonTooltip = StringsStore().registeredStringModules.get("common").strings.get("openContextMenuButtonTooltip");
            ScheduleCell._shiftLookupContextMenuButtonAriaLabel = StringsStore().registeredStringModules.get("common").strings.get("openShiftLookupMenuButtonAriaLabel");
            ScheduleCell._shiftLookupContextMenuButtonTooltip = StringsStore().registeredStringModules.get("common").strings.get("openShiftLookupMenuButtonTooltip");
        }

        this.state = {
            isMoreMenuCalloutVisible: false,
            isShiftLookupCalloutVisible: false,
            isOpenShiftRequestCalloutVisible: false,
            instrumentationAction: "",
            triggerKey: "",
            isConflictCalloutVisible: false,
            renderExtras: false,
            isDragOver: false,
            isDragging: false
        };
    }

    componentDidUpdate(prevProps: IBaseScheduleCellProps, prevState: ScheduleCellState) {
        const { instrumentScheduleEvent, shift, currentUserMemberId, getNumCellsSelected } = this.props;
        const { isMoreMenuCalloutVisible, isShiftLookupCalloutVisible, isOpenShiftRequestCalloutVisible, instrumentationAction } = this.state;

        // If the shift identity changed while we think we're dragging, a MobX store update
        // caused a remount/replacement of this cell mid-drag. Clear the stale dragging state
        // so the cell doesn't stay faded at opacity 0.5.
        if (this.state.isDragging && prevProps.shift?.id !== this.props.shift?.id) {
            this.setState({ isDragging: false });
        }

        // if re-render is happening because of Time off request approval, old shift and new shift will differ by shift type.
        // If thats the case, dismiss the context menu ( in case it is still open )
        // Hide conflict menu if its open while re-render
        if (prevProps.shift && this.props.shift && ShiftUtils.isTimeOffRequestEvent(prevProps.shift) && ShiftUtils.isTimeOffEvent(this.props.shift)) {
            this.hideMoreMenu();
            this.hideConflictMenu();
            this.hideOpenShiftRequestApprovalCallout();
        }

        // Attach/detach the keyboard listener that invokes the contextual menu when the schedule cell
        // acquires/loses focus
        // TODO (DCoh): Implement a scenario test that verifies keyboard-based focus handling and contextual menu invoking for schedule cells.
        // Verify:
        // - Tab forward to next schedule cell, hit enter to invoke context menu
        // - Tab backward (shift tab) to previous schedule cell, hit enter to invoke context menu (bug 589797)
        if (this.props.cellHasFocus && !prevProps.cellHasFocus) {  // on focus
            this.attachKeyboardListeners();
        } else if (!this.props.cellHasFocus && prevProps.cellHasFocus) {  // on blur
            this.detachKeyboardListeners();
        }

        // protect against instrumenting the same action multiple times - only instrument if a callout/menu was opened that
        // previously was not open
        if (instrumentScheduleEvent
            && (isMoreMenuCalloutVisible && !prevState.isMoreMenuCalloutVisible)
            || (isShiftLookupCalloutVisible && !prevState.isShiftLookupCalloutVisible)
            || (isOpenShiftRequestCalloutVisible && !prevState.isOpenShiftRequestCalloutVisible)) {
                if (this.shouldShowTimeOffRequestApprovalCallout(shift)) {
                    instrumentScheduleEvent(InstrumentationService.events.Requests, [
                        getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.RequestsEntryPointClicked),
                        getGenericEventPropertiesObject(InstrumentationService.properties.Location, InstrumentationService.values.Flyout),
                        getGenericEventPropertiesObject(InstrumentationService.properties.RequestType, InstrumentationService.ShiftRequestTypes.TimeOff),
                        getGenericEventPropertiesObject(InstrumentationService.properties.EntryPoint, instrumentationAction)
                    ]);
                } else if (this.shouldShowShiftContextMenu(shift)) {
                    const numCells = getNumCellsSelected();
                    const isMultiSelectMenu = !isShiftLookupCalloutVisible && numCells > 1;
                    const contextMenuEventProperties = [
                        getGenericEventPropertiesObject(InstrumentationService.properties.CellType, InstrumentationUtils.getInstrumentationCellType(shift)),
                        getGenericEventPropertiesObject(InstrumentationService.properties.ActionType, instrumentationAction),
                        getGenericEventPropertiesObject(InstrumentationService.properties.IsMultiSelect, isMultiSelectMenu)
                    ];
                    if (isMultiSelectMenu) {
                        contextMenuEventProperties.push(getGenericEventPropertiesObject(InstrumentationService.properties.SelectedCellCount, numCells));
                    }
                    instrumentScheduleEvent(isShiftLookupCalloutVisible ? InstrumentationService.events.ShiftLookupOpened : InstrumentationService.events.ContextMenuOpened, contextMenuEventProperties);
                } else if (this.shouldShowShiftDetailsCallout(shift)) {
                    // opening the shift details callout will trigger a requests instrumentation if the shift is an open shift
                    // it will also trigger shift details instrumentation for assigned and open shifts
                    if (ShiftUtils.isOpenShift(shift)) {
                        instrumentScheduleEvent(InstrumentationService.events.Requests, [
                            getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.RequestsEntryPointClicked),
                            getGenericEventPropertiesObject(InstrumentationService.properties.Location, InstrumentationService.values.Flyout),
                            getGenericEventPropertiesObject(InstrumentationService.properties.RequestType, InstrumentationService.ShiftRequestTypes.EmployeeOpenShift),
                            getGenericEventPropertiesObject(InstrumentationService.properties.EntryPoint, instrumentationAction)
                        ]);
                    }
                    instrumentScheduleEvent(InstrumentationService.events.EmployeeShiftDetails, [
                        getGenericEventPropertiesObject(InstrumentationService.properties.OwnShift, shift.memberId === currentUserMemberId)]);
                }
        }
    }

    /**
     * Return true if Shift Context Menu should be shown.
     * This is the case if selection is enabled, the contextual menu is enabled, the menu
     * is visible through more button click or right click, and this is either an empty cell or
     * a working shift cell (open or assigned shift) or a time off shift cell. Time off request cells
     * should never show the contextual menu.
     * @param shift
     */
    private shouldShowShiftContextMenu(shift: IBaseShiftEntity): boolean {
        const { selectionEnabled, enableContextualMenu } = this.props;
        return ((this.state.isMoreMenuCalloutVisible || this.state.isShiftLookupCalloutVisible) && selectionEnabled && enableContextualMenu &&
                (!shift || ShiftUtils.isWorkingShift(shift) || ShiftUtils.isTimeOffEvent(shift)));
    }

    /**
     * Returns true if the extras (more icon, search icon, context menus) should be rendered. For performance it is VERY IMPORTANT we
     * don't render the extras unless absolutely needed (such as when the cell is focused or hovered).
     * @returns boolean
     */
    private shouldRenderExtras(): boolean {
        return (this.props.cellHasFocus || this.state.renderExtras);
    }

    /**
     * Returns true if Time off request callout should be shown.
     * This is the case if selection is enabled, the callout is visible through more button click or right click,
     * and the shift in the cell is a timeoff request event.
     * @param shift
     */
    private shouldShowTimeOffRequestApprovalCallout(shift: IBaseShiftEntity): boolean {
        return shift && this.props.selectionEnabled && (this.state.isMoreMenuCalloutVisible || this.state.isShiftLookupCalloutVisible) && ShiftUtils.isTimeOffRequestEvent(shift);
    }

    /**
     * Returns true if conflict callout should be shown.
     * This is the case if selection is enabled, the callout is visible on conflict icon click
     * @param shift
     */
    private shouldShowConflictDetailsCallout(shift: IBaseShiftEntity): boolean {
        // never render conflict callout for empty cell
        if (!shift) {
            return false;
        }
        const currentUser = TeamStore() && TeamStore().me;
        return ConflictUtils.isConflictEnabledForAdminInDateRange(MemberUtils.isAdmin(currentUser), schedulesViewStateStore().viewEndDate) && (this.state.isConflictCalloutVisible);
    }

    /**
     * Returns true if the shift details callout should be shown.
     * @param shift
     */
    private shouldShowShiftDetailsCallout(shift: IBaseShiftEntity): boolean {
        return FlightSettingsService.isFlightEnabled(FlightKeys.EnableOpenShiftRequestsOnWeb)
                    && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableOpenShiftRequests)
                    && shift
                    && this.props.showShiftDetails
                    && (this.state.isMoreMenuCalloutVisible || this.state.isShiftLookupCalloutVisible);
    }

    /**
     * Returns true if the open shift request approval callout should be shown
     */
    private shouldShowOpenShiftRequestCallout(): boolean {
        return this.state.isOpenShiftRequestCalloutVisible && FlightSettingsService.isFlightEnabled(FlightKeys.EnableOpenShiftRequestsOnWeb);
    }

    private attachKeyboardListeners() {
        if (!this._combokeys) {
            // Note: Keyboard handling should be attached to an element inside the schedule grid rather than the document level.
            // This would help us to avoid triggering this keypress handler for popups and modals.
            // Another alternative would be to attach a key event listener to each cell. However, having a listener for each cell
            // might be expensive, and also the listener would actually need to be attached to the FlexiCell parent, since that's
            // what actually gets selection cell focus. (also, the base context menu setup should also be moved up to FlexiCell)
            // Currently as a workaround, we use ScheduleGridUtils.isFocusInScheduleGridCellOrShiftMenu() to avoid handling keypresses
            // when focus is not inside a schedule cell.
            this._combokeys = new Combokeys(document.documentElement);
            this._combokeys.bind(['any-character', 'enter', 'space', 'tab'], this.onKeyDown);
        }
    }

    componentDidMount() {
        const el = this._cellElement.current;
        if (el) {
            // Listen in CAPTURE phase so drag setup runs before any child stopPropagation()
            // call can block React's bubble-phase synthetic onDragStart from firing.
            // All drag setup (ghost, dragState, isDragging) happens here so that the drag
            // works correctly even if React's onDragStart never fires (the symptom: only
            // onDragEnd logs appear, no onDragStart, caret ghost, stop cursor).
            this._nativeDragStartHandler = (e: DragEvent) => {
                // Block new drags while a drop operation (e.g. the two-step open-shift assign) is in-flight.
                if (isDropOperationPending()) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
                // If the interaction started on the padding area around the shift card
                // (not on the card itself), cancel the drag so multi-select can work instead.
                // NOTE: do NOT clear _paddingMouseDown here — React's synthetic onDragStart
                // still fires after e.preventDefault() and needs to see the flag to guard
                // against setting isDragging:true in the fallback path.
                if (this._paddingMouseDown) {
                    e.preventDefault();
                    return;
                }
                const { shift, member, tagId } = this.props;
                // Block re-drag of the specific open shift being assigned until the API completes
                // and the store reflects the updated slot count. Other shifts are unaffected.
                if (shift && isOpenShiftAssignPending(shift.id)) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
                // Any non-empty drag-enabled cell can be a drag source — no prior selection required.
                const isDragSource = this.props.isDragEnabled && !this.doRenderEmptyCell();
                if (!isDragSource) {
                    // Non-source cell: cancel any native drag from child elements (SVG, img, text)
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                if (!shift) { return; }

                // Full drag setup in capture phase — guaranteed to run regardless of what
                // child components do in the bubble phase.
                window.getSelection()?.removeAllRanges();
                (document.activeElement as HTMLElement)?.blur();

                // Off-screen clone as ghost: cloneNode never has a text caret since it
                // doesn't copy live focus/selection state.
                const clone = el.cloneNode(true) as HTMLElement;
                clone.style.cssText = `position:fixed;top:-9999px;left:0;width:${el.offsetWidth}px;height:${el.offsetHeight}px;pointer-events:none;`;
                // Append inside the app tree (not document.body) so Teams/Fluent CSS custom
                // properties (font-family, theme tokens) cascade into the clone correctly.
                // position:fixed keeps it off-screen regardless of DOM parent.
                const ghostParent = el.parentElement || document.body;
                ghostParent.appendChild(clone);
                const rect = el.getBoundingClientRect();
                const offsetX = e.clientX - rect.left;
                const offsetY = e.clientY - rect.top;
                e.dataTransfer.setDragImage(clone, offsetX, offsetY);
                requestAnimationFrame(() => ghostParent.contains(clone) && ghostParent.removeChild(clone));

                e.dataTransfer.effectAllowed = "move";
                setDragState(shift, member, tagId);

                // In day view, record where in the shift the user grabbed (in minutes from its
                // start) so that onDrop on the source cell can compute the correct new start time,
                // enabling small positional adjustments that stay within the original cell's range.
                if (this.props.scheduleCalendarType === ScheduleCalendarTypes.Day) {
                    const shiftDurationMinutes = shift.endTime.diff(shift.startTime, "minutes");
                    const grabTime = this.getDayViewCursorTime(e.clientX, rect);
                    if (shiftDurationMinutes > 0 && grabTime) {
                        // Round to the nearest 30-min drop cell interval and clamp to
                        // [0, shiftDurationMinutes] so non-multiples-of-30 durations can't
                        // produce an offset that overshoots the shift's own length.
                        const rawOffset = grabTime.diff(shift.startTime, "minutes", true);
                        const roundedOffset = Math.round(rawOffset / 30) * 30;
                        setDayViewGrabOffsetMinutes(Math.max(0, Math.min(roundedOffset, shiftDurationMinutes)));
                    }
                }

                this.setState({ isDragging: true });
                // Deselect this cell so Fabric's selection highlight doesn't persist on the
                // source cell after the drop. SelectionZone selects on mousedown, so we
                // clear it here when the drag actually starts.
                this.setGridCellSelected(false);

                // For time-off shifts, defer ShiftDragStarted until the first valid onDragEnter.
                // This prevents logging if the drag never hovers a valid target (e.g. dragged
                // to a different member's row, which is blocked for time-off).
                if (!ShiftUtils.isTimeOffEvent(shift)) {
                    this.props.instrumentScheduleEvent(InstrumentationService.events.DragAndDrop_ShiftDragStarted, [
                        getGenericEventPropertiesObject(InstrumentationService.properties.CellType, InstrumentationUtils.getInstrumentationCellType(shift))
                    ]);
                }
            };
            el.addEventListener("dragstart", this._nativeDragStartHandler, true /* capture */);
        }
    }

    componentWillUnmount() {
        this.detachKeyboardListeners();
        if (this.state.isDragging) {
            // This cell is the drag source unmounting mid-drag (e.g. a MobX store update
            // remounts the row). Ownership of the singleton preview may have moved to the
            // currently hovered target, and this source will no longer receive dragend, so
            // clear the preview globally to avoid leaving an orphaned element behind.
            clearMultiDayDropPreview();
        } else {
            clearMultiDayDropPreview(this.getDropPreviewOwnerId());
        }
        const el = this._cellElement.current;
        if (el && this._nativeDragStartHandler) {
            el.removeEventListener("dragstart", this._nativeDragStartHandler, true);
        }
        this._nativeDragStartHandler = null;
        // Remove any pending padding-mouseup listener to prevent restoring draggable
        // on a now-unmounted element.
        document.removeEventListener("mouseup", this._onPaddingMouseUp);
        if (el) {
            el.removeEventListener("mouseleave", this._onPaddingMouseUp);
            el.removeEventListener("pointercancel", this._onPaddingMouseUp);
        }
        // Clear module-level drag state if this component unmounts mid-drag
        // (e.g. MobX store update causes remount), so other cells don't get
        // stuck in a no-drop state.
        if (this.state.isDragging) {
            clearDragState();
        }
    }

    /**
     * Restores draggable=true on the cell after a padding-area mousedown.
     * Called via a one-time document mouseup listener set in onMouseDown.
     */
    private _onPaddingMouseUp = () => {
        const el = this._cellElement.current;
        if (el) {
            el.draggable = this.props.isDragEnabled && !this.doRenderEmptyCell();
        }
    };

    private detachKeyboardListeners() {
        if (this._combokeys) {
            this._combokeys.detach();
            this._combokeys = null;
        }
    }

    /**
     * Handles opening the default context menu (more menu), when a key is pressed on a schedule cell.
     * @param e - KeyboardEvent
     */
    private onKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        const nonActionableTarget = target && target.nodeName.toLowerCase() !== "button";
        const { getNumCellsSelected, showShiftDetails } = this.props;

        // Adding prevent default and stop propagation to prevent Selection Zone from handling "space" keypress to toggle it's focus.
        if (KeyboardUtils.isSpacePressed(e) && nonActionableTarget) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Prevent losing the More Icons menu when pressing TAB and temporarily losing the cell focus
        if (KeyboardUtils.isTabPressed(e)) {
            this.setState({ renderExtras: true });
        }

        if (target?.classList?.contains("requestsIndicator")) {
            // if the keydown was on the requests indicator, don't handle it here
            return;
        }

        if ((!this.isAnyCalloutOpen())   // none of the context menus are open
            && !KeyboardUtils.isCtrlOrCommandPressed(e)                                         // the ctrl or command key is not pressed
            && !KeyboardUtils.isTabPressed(e)                                                   // the tab key is not pressed
            && ScheduleGridUtils.isFocusInScheduleGridCellOrShiftMenu()                         // focus is in the grid or one of the context menus
            && (getNumCellsSelected() === 1 || showShiftDetails)) {                             // only 1 cell is selected
                // if this is not an action key (enter or space), show the shift lookup menu and pass the key
                // that triggered the menu so it can be passed to the search control
                // regression #510477
                const showShiftLookupMenu = !KeyboardUtils.isActionKeyPressed(e);
                this.showCellCallout(InstrumentationService.actionTypes.Type, showShiftLookupMenu, false /*isConflictCallout*/, e.key);  // show the context menu
        }
    }

    /**
     * Callback for when the shift lookup context menu button is activated via click
     */
    private onShiftLookupIconClicked = () => {
        this.showCellCallout(InstrumentationService.actionTypes.Mouse, true, false /*isConflictCallout*/);
    }

    /**
     * Callback for when the context menu button is activated via keypress
     * @param key
     */
    private onShiftLookupKeyPressed = (key: React.KeyboardEvent<HTMLDivElement>) => {
        if (KeyboardUtils.isActionKeyPressed(key)) {
            key.preventDefault();
            key.stopPropagation();
            this.showCellCallout(InstrumentationService.actionTypes.EnterKey, true, false /*isConflictCallout*/ );
        }
    }

    /**
     * Callback for when the conflict icon button is activated via click
     */
    private onConflictIconClicked = () => {
        this.showCellCallout(InstrumentationService.actionTypes.EnterKey, false, true /*isConflictCallout*/ );
        this.instrumentConflictIconClicked();
    }

    /**
     * Instrument a click on conflict Icon
     */
    private instrumentConflictIconClicked() {
        InstrumentationService.logEvent(InstrumentationService.events.ConflictBadgeIconClicked,
           [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(schedulesViewStateStore().scheduleCalendarType))]);
   }

    /**
     * Callback for when the context menu button is activated via click
     */
    private onMoreIconClicked = () => {
        this.showCellCallout(InstrumentationService.actionTypes.Mouse, false, false /*isConflictCallout*/);
    }

    /**
     * Callback for when the context menu button is activated via keypress
     * @param key
     */
    private onMoreIconKeyPressed = (key: React.KeyboardEvent<HTMLDivElement>) => {
        if (KeyboardUtils.isActionKeyPressed(key)) {
            key.preventDefault();
            key.stopPropagation();
            this.showCellCallout(InstrumentationService.actionTypes.EnterKey, false, false /*isConflictCallout*/);
        }
    }

    /**
     * Handler when the cell is right clicked
     * @param e - React.MouseEvent
     */
    private onRightClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        const { selectionEnabled, enableContextualMenu } = this.props;

        // Right clicks should only attempt to open a callout if selection and the contextual menu are enabled
        if (selectionEnabled && enableContextualMenu) {
            this.showCellCallout(InstrumentationService.actionTypes.Mouse, false, false /*isConflictCallout*/);
        }
    }

    /**
     * Handler when the cell is left clicked
     * @param e - React.MouseEvent
     */
    private onLeftClick = (e: React.MouseEvent<HTMLElement>) => {
        const { selectionEnabled, showShiftDetails } = this.props;

        // Left clicks should only attempt to open a callout if selection is disabled and showShiftDetails is true
        // This is because when selection is enabled left clicks act to select the cell
        // Also open the callout if it is a time off request.
        if ((!selectionEnabled && showShiftDetails) || ShiftUtils.isTimeOffRequestEvent(this.props.shift))  {
            this.showCellCallout(InstrumentationService.actionTypes.Mouse, false, false /*isConflictCallout*/);
        }
    }

    /**
     * Callback that displays the contextual menu, the time off request callout,
     * or the shift details callout
     * @param actionType (InstrumentationService.actionTypes) The instumentation action
     * @param isShiftLookup Pass true to show the shift lookup menu, otherwise the ... more menu is shown
     * @param isConflictCallout Boolean to show conflict callout menu
     * @param triggerKey (optional) The key that triggered the callout. Is used by the shift lookup menu to pass as the initial value for the search field.
     */
    private showCellCallout(actionType: string, isShiftLookup: boolean, isConflictCallout: boolean, triggerKey?: string) {

        if (this._cellElement) {
            // Set the main cell callout visible and hide the open shift request approval callout in case it was open
            if (isShiftLookup) {
                // pass the key that triggered (opened) the shift lookup menu so that it can be passed
                // to the search control
                this.setShiftLookupMenuVisible(true, triggerKey);
                this.setConflictCalloutVisible(false);
            } else if (isConflictCallout) {
                this.setConflictCalloutVisible(true);
                this.setMoreMenuVisible(false);
            } else {
                this.setConflictCalloutVisible(false);
                this.setMoreMenuVisible(true);
            }

            this.hideOpenShiftRequestApprovalCallout();

            // set the actionType in state so it will be logged in instrumentation
            this.setState({ instrumentationAction: actionType });
        }
    }

    private getSlotBarProps(): SlotBarProps {
        const slotBarProps: SlotBarProps = this.props.showSlotBar ? ShiftUtils.getSlotBarProps(this.props.shift, this.props.scheduleCellRenderSize) : null;
        // Because our slot bar is rendered in the ShiftThemeContainer, we need to pass the showConflictIcon prop to the ShiftThemeContainer
        // so the CLS icon can be rendered properly, slotBarProps.showConflictIcon is only needed for the slot bar and where conflicts are shown
        const showConflictIcon = this.shouldShowConflictIcon();
        if (slotBarProps) {
            slotBarProps.showConflictIcon = showConflictIcon;
        }
        return slotBarProps;
    }

    /**
     * Function that will set this schedule cell's selection state in the Selection object contained within the schedule grid
     * @param isSelected
     */
    private setGridCellSelected = (isSelected: boolean) => {
        if (this.props.setGridCellSelected && this.props.dataSelectionIndexVal) {
            this.props.setGridCellSelected(this.props.dataSelectionIndexVal, isSelected);
        }
    }

    /**
     * Cells in open shift rows will use the openshifts tour marker, while other cells will use the moreIcon marker.
     * This is used by the TourGuide to find elements in the DOM to attach tour stop bubbles to.
     */
    private getStaffHubTourMarker() {
        return this.props.inOpenShiftRow ? null : StaffHubTourMarker.assignedshifts;
    }

    /**
     * Opens or closes the more menu context menu
     */
    private setMoreMenuVisible = (visible: boolean) => {
        if (visible) {
            // make sure the shift lookup menu is closed
            this.setState({ isShiftLookupCalloutVisible: false });
        }
        this.setState({ isMoreMenuCalloutVisible: visible });
    }

    /**
     * Opens or closes conflict callout menu
     */
    private setConflictCalloutVisible = (visible: boolean) => {
        if (visible) {
            // make sure the shift lookup menu is closed
            this.setState({ isShiftLookupCalloutVisible: false, isMoreMenuCalloutVisible: false });
        }
        this.setState({ isConflictCalloutVisible: visible });
    }

    // The conflict callout menu is hidden when the user 'clicks' elsewhere on the screen
    private hideConflictMenu = () => {
        this.setConflictCalloutVisible(false);
    }

    /**
     * Opens or closes the shift lookup context menu
     * @param visible True to show the callout, false to hide it
     * @param key The key that triggered the callout. Is used by the shift lookup menu to pass as the initial value for the search field.
     */
    private setShiftLookupMenuVisible = (visible: boolean, key: string) => {
        if (visible) {
            // make sure the more menu is closed
            this.setState({ isMoreMenuCalloutVisible: false });
        }
        this.setState({ isShiftLookupCalloutVisible: visible, triggerKey: key });
    }

    // The contextual menu is hidden when the user 'clicks' elsewhere on the screen
    private hideMoreMenu = () => {
        this.setMoreMenuVisible(false);
    }

    // The contextual menu is hidden when the user 'clicks' elsewhere on the screen
    private hideShiftLookupMenu = () => {
        this.setShiftLookupMenuVisible(false, null /*key*/);
    }

    /**
     * Returns true if the shift has been newly edited or added
     */
    private isShiftNewlyEdited() {
        return this.props.isEntityNewlyEdited && this.props.shift && this.props.isEntityNewlyEdited(this.props.shift.id);
    }

    /**
     * Renders the ... more icon in the cell. Positioned in the center for day cells and on the right for
     * other cells.
     */
    private renderMoreIcon() {
        const { shift, enableContextualMenu, selectionEnabled, scheduleCalendarType, hasConflicts } = this.props;

        let locationStyle;
        const isConflictingShift = hasConflicts;

        if ((scheduleCalendarType === ScheduleCalendarTypes.Day || this.doRenderEmptyCell()) && scheduleCalendarType !== ScheduleCalendarTypes.Week
            && scheduleCalendarType !== ScheduleCalendarTypes.Fortnight) {
            locationStyle = styles.center;
        } else if (scheduleCalendarType === ScheduleCalendarTypes.Month || isConflictingShift
            && (scheduleCalendarType === ScheduleCalendarTypes.Week || scheduleCalendarType === ScheduleCalendarTypes.Fortnight)) {
            // When there is a conflict, the more menu will be similar to as displayed in month view
            locationStyle = styles.bottom;
        } else {
            locationStyle = styles.right;
        }

        const moreIconStyles = classNames(
             ScheduleGridCssClasses.moreIcon,
            { [`${styles.visible}`]: this.state.isMoreMenuCalloutVisible },
            locationStyle);

        return (
               // Context menu button is rendered if selection and the contextual menu are enabled (enabledContextualMenu is also used to enable time off request callouts)
                enableContextualMenu && selectionEnabled &&
                <div
                    data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDMoreScheduleElement") }
                    className={ classNames(moreIconStyles, { [`${styles.visible}`]: this.props.cellHasFocus }) }>
                    <IconButton
                        componentRef= { this._shiftLookupElement }
                        title={ ScheduleCell._shiftLookupContextMenuButtonTooltip }
                        ariaLabel={ ScheduleCell._shiftLookupContextMenuButtonAriaLabel }
                        onKeyPress={ this.onShiftLookupKeyPressed }
                        onClick={ this.onShiftLookupIconClicked }
                        className={ classNames(styles.iconButton, { [`${styles.visibilityHidden}`]: ShiftUtils.isTimeOffRequestEvent(shift) }) }
                        data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDSearchIconButton") }
                        iconProps={ { styles: { root: styles.panelCloseButtonIcon }, iconName: "Search" } } />
                    <IconButton
                        title={ ScheduleCell._contextMenuButtonTooltip }
                        ariaLabel={ ScheduleCell._contextMenuButtonAriaLabel }
                        onKeyPress={ this.onMoreIconKeyPressed }
                        className={ classNames(styles.iconButton, this.getStaffHubTourMarker()) }
                        onClick={ this.onMoreIconClicked }
                        data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDMoreIconButton") }
                        iconProps={ { styles: { root: styles.panelCloseButtonIcon }, iconName: "More" } } />
                </div>
        );
    }

    private shouldRenderUnsharedStatus(): boolean {
        const { shift } = this.props;
        const isTimeOffShift = ShiftUtils.isTimeOffRequestEvent(shift);
        // ignore unshared edits for predraft shifts
        return !isTimeOffShift && this.props.showUnsharedStatus && ShiftUtils.shiftHasUnsharedEdits(shift) && !shift.isPredraft;
    }

    /**
     * Renders the asterisk on unshared shifts.
     */
    private renderUnsharedStatus(): JSX.Element {
        const { shift } = this.props;

        const renderUnsharedStatus = this.shouldRenderUnsharedStatus();

        return renderUnsharedStatus ? (
            <EditStatusIndicator
                themeClass={ styles[ShiftUtils.getShiftThemeClass(shift)] }
                className={ classNames(styles.asterisk, { [styles.dayView]: this.props.scheduleCellRenderSize === ScheduleCellRenderSize.Large }) }
                isNewlyEdited={ false }
                dataAutomationId={ AutomationUtils.getAutomationId("scheduler", "QAIDShiftUnsharedIndicator") } />
        ) : null;
    }

    /**
     * Determines if the shift has conflicts and if the conflict icon should be shown
     */
    private shouldShowConflictIcon(): boolean {
        return this.props.hasConflicts && !this.props.isSharedScheduleView && ConflictUtils.showConflictIcon(this.props.shift);
    }

    private renderShift(): JSX.Element {
        const { shift, scheduleCellRenderSize, showUnsharedStatus, showIcons, showNotes, inOpenShiftRow, selectionEnabled, showActivities, isViewGrouped, show24HrShift, hideTitleFor24HrShift, isAdmin} = this.props;
        const shiftThemeClass = ShiftUtils.getShiftThemeClass(shift);
        const isWorkingShift = ShiftUtils.isWorkingShift(shift);
        const isTimeOffShift = ShiftUtils.isTimeOffEvent(shift);
        const isCrossLocationShift = ShiftUtils.isCrossLocationShift(shift) && !isAdmin;
        const showConflictIcon = this.shouldShowConflictIcon();
        return (
            <>
                { this.renderUnsharedStatus() }
                <ShiftThemeContainer
                    theme={ shiftThemeClass }
                    slotBarProps={ this.getSlotBarProps() }
                    nonClickable={ !selectionEnabled }
                    isTimeOff={ isTimeOffShift }
                    showDashedLeftBorder={isCrossLocationShift || shift.isPredraft}
                    shaded={shift.isPredraft}>

                    <div className={ ScheduleCell._contentClasses }>
                        {
                            isWorkingShift &&
                                <Shift
                                    shift={ shift }
                                    isNewlyEdited={ this.isShiftNewlyEdited() }
                                    // predraft has no concept of unshared edits
                                    showUnsharedStatus={ showUnsharedStatus && !shift.isPredraft }
                                    scheduleCellRenderSize={ scheduleCellRenderSize }
                                    doShowIcons={ showIcons }
                                    doShowNotes={ showNotes }
                                    doShow24HrShift={ show24HrShift }
                                    showGroupName={ !isViewGrouped }
                                    hideTitleFor24HrShift={ hideTitleFor24HrShift }
                                    onConfictIconBadgeClick={ this.onConflictIconClicked }
                                    doShowConflictIcon={ showConflictIcon }
                                    crossLocationTeamId={ ShiftUtils.getCrossLocationTeamId(this.props.shift, this.props.isAdmin) } />
                        }
                        {
                            !isWorkingShift && isTimeOffShift &&
                                <TimeOffShift
                                    isNewlyEdited={ this.isShiftNewlyEdited() }
                                    shift={ shift }
                                    scheduleCellRenderSize={ scheduleCellRenderSize }
                                    onConfictIconBadgeClick={ this.onConflictIconClicked }
                                    doShowConflictIcon={ showConflictIcon } />
                        }
                        {
                            !isWorkingShift && !isTimeOffShift &&
                                <TimeOffRequestShift
                                    shift={ shift }
                                    scheduleCellRenderSize={ scheduleCellRenderSize } />
                        }
                        { this.shouldRenderExtras() && this.renderMoreIcon() }
                        {
                            showActivities &&
                                <ActivityFlexContainer
                                    shift={ shift }
                                    hideActivityCodes={ inOpenShiftRow }
                                    selectionEnabled={ selectionEnabled }
                                    viewStart={ this.props.viewStart }
                                    viewEnd={ this.props.viewEnd }
                                    setFlexItemSelected={ this.props.setFlexItemSelected }
                                    getFlexItemSelected={ this.props.getFlexItemSelected }
                                    setGridCellSelected={ this.setGridCellSelected }/>
                        }
                    </div>
                </ShiftThemeContainer>
            </>
            );
    }

    /**
     * Renders any active callout that should open on the cell
     */
    private renderCallout() {
        const { shift, navigateToRequestPage, instrumentScheduleEvent, isAdmin, scheduleCalendarType } = this.props;
        const groupedRequest: IGroupedOpenShiftRequestEntity = this.getGroupedShiftRequestForThisCell();
        const shiftRequestId: string = ShiftRequestUtils.getFirstShiftRequestIdFromGroupedRequest(groupedRequest);

        let conflictEntities: IBaseConflictEntity[] = [];
        // make sure you handle empty cell

        const currentUser = TeamStore() && TeamStore().me;
        if (ConflictUtils.isConflictEnabledForAdminInDateRange(MemberUtils.isAdmin(currentUser), schedulesViewStateStore().viewEndDate) && shift) {
            const shiftIdToConflictsMap = schedulesViewStateStore().conflictsInView;
            conflictEntities = shiftIdToConflictsMap && shiftIdToConflictsMap.get(shift.id);
        }

        return (
            <>
                { this.renderContextualMenu(this.getCellStartOfDay(), shift) }
                {
                    this.shouldShowTimeOffRequestApprovalCallout(shift) &&
                        <TimeOffRequestApprovalCallout
                            navigateToRequestPage={ navigateToRequestPage }
                            targetElement={ this._cellElement.current }
                            onDismissCallback={ this.hideMoreMenu }
                            shift={ shift }
                            instrumentScheduleEvent= { this.props.instrumentScheduleEvent }/>
                }
                {
                    this.shouldShowConflictDetailsCallout(shift) &&
                        <ConflictDetailsCallout
                            targetElement={ this._cellElement.current }
                            onDismissCallback={ this.hideConflictMenu }
                            shiftId={ shift.id }
                            conflictEntities={ conflictEntities }
                            scheduleCalendarType={ scheduleCalendarType }
                            elementToFocusOnDismiss={ this._shiftLookupElement } />
                }
                {
                    this.shouldShowShiftDetailsCallout(shift) &&
                        <ShiftDetailsCallout
                            onDismissCallback={ this.hideMoreMenu }
                            targetElement={ this._cellElement.current }
                            setInitialFocus={ true }
                            className={ AutomationUtils.getAutomationId("scheduler", "QAIDShiftDetailsCallout") }
                            shiftDetailsProps={ {
                                shift: shift,
                                requestId: shiftRequestId,
                                navigateToRequestPage: navigateToRequestPage,
                                instrumentScheduleEvent: instrumentScheduleEvent,
                                isAdmin: isAdmin,
                                scheduleCalendarType: scheduleCalendarType
                            } }/>
                }
                {
                    this.shouldShowOpenShiftRequestCallout() &&
                        <OpenShiftRequestApprovalCallout
                            navigateToRequestPage={ navigateToRequestPage }
                            groupedRequest={ groupedRequest }
                            onDismissCallback={ this.hideOpenShiftRequestApprovalCallout }
                            targetElement={ this._cellElement.current }
                            />
                }
            </>
        );
    }

    /**
     * Returns a moment to be used as the start of the day for the current cell. If we receive
     * a shift via props, we use the start of the day for that shift, otherwise we use the
     * startOfDay prop.
     */
    private getCellStartOfDay(): Moment {
        const { startOfDay, shift } = this.props;
        return shift ? shift.startTime.clone().startOf("date") : startOfDay;
    }

    /**
     * Renders an empty cell
     */
    private renderEmptyCell() {
        const { scheduleCalendarType, shift } = this.props;

        return (
            <>
                {
                    this.props.availability &&
                        <EmptyCellAvailability
                            availability={ this.props.availability }
                            scheduleCalendarType={ scheduleCalendarType }/>
                }
                { /** Deleted shifts with active shared changes will be rendered as empty cells with an edit status indicator */
                    ShiftUtils.isUnsharedDeletedShift(shift) && this.shouldRenderUnsharedStatus() &&
                        <EditStatusIndicator
                            className={ styles.deletedShiftWithSharedChangesIndicator }
                            isNewlyEdited={ this.isShiftNewlyEdited() }
                            ariaLabel={ ShiftUtils.getUnsharedChangesShiftIndicatorAriaLabel() }
                            dataAutomationId={ AutomationUtils.getAutomationId("scheduler", "QAIDDeletedShiftUnsharedIndicator") } />
                }
                {
                   this.shouldRenderExtras() && this.renderMoreIcon()
                }
            </>
        );
    }

    /**
     * Renders the contextual menu, if a date has been provided and the contextual menu should be shown
     */
    private renderContextualMenu(date: Moment, shift: IShiftEntity) {
        const { scheduleCalendarType, inOpenShiftRow, member, tagId, scheduleCellActionCallbacks, disableShare } = this.props;
        // Use either the tagId specified by props (grouped view), the tagId from the shift (ungrouped view), or an empty string
        const tagIdForMenu = tagId || (shift && ShiftUtils.getTagIdFromShift(shift)) || "";
        const shouldShowShiftContextMenu = this.shouldShowShiftContextMenu(shift);
        return (
            shouldShowShiftContextMenu && date &&
                <ShiftContextualMenu
                    isShiftLookup={ this.state.isShiftLookupCalloutVisible }
                    triggerKey={ this.state.triggerKey }
                    member={ member }
                    groupTagId ={ tagIdForMenu }
                    shift={ shift }
                    isOpenShift={ inOpenShiftRow }
                    disableShare={ disableShare }
                    date={ date }
                    target={ this._cellElement.current }
                    onDismissCallback={ this.state.isShiftLookupCalloutVisible ? this.hideShiftLookupMenu : this.hideMoreMenu }
                    scheduleCellActionCallbacks={ scheduleCellActionCallbacks }
                    scheduleCalendarType={ scheduleCalendarType }
                    dataSelectionIndexVal={ this.props.dataSelectionIndexVal }
                    isMultiSelect={ this.props.getNumCellsSelected() > 1 } />
        );
    }

    shouldComponentUpdate(nextProps: IBaseScheduleCellProps, nextState: ScheduleCellState): boolean {
        // optimzation for empty cells
        if ((!this.props.shift && !nextProps.shift)                                         // no shift change
            && this.props.scheduleCalendarType === nextProps.scheduleCalendarType           // no calendar type change
            && this.state.isMoreMenuCalloutVisible === nextState.isMoreMenuCalloutVisible   // context menu not visible
            && this.state.isOpenShiftRequestCalloutVisible === nextState.isOpenShiftRequestCalloutVisible   // context menu not visible
            && this.state.isShiftLookupCalloutVisible === nextState.isShiftLookupCalloutVisible   // context menu not visible
            && this.props.cellHasFocus === nextProps.cellHasFocus                           // no focus change
            && this.state.isConflictCalloutVisible === nextState.isConflictCalloutVisible   // conflict callout not visible
            && this.state.renderExtras === nextState.renderExtras
            && this.state.isDragOver === nextState.isDragOver                               // no drag-over change
            && this.state.isDragging === nextState.isDragging                               // no dragging change
            && AvailabilityUtils.areEqual(this.props.availability, nextProps.availability)  // no change to availabilities
            ) {
                return false;
            }

        return true;
    }

    /**
     * This function will render an indicator component to display the presence of open shift requests
     */
    private renderShiftRequestsIndicator(): JSX.Element {
        const { scheduleCellRenderSize, shift } = this.props;

        const groupedShiftRequest: IGroupedOpenShiftRequestEntity = this.getGroupedShiftRequestForThisCell();

        // only render the requests indicator is the open shift is published
        const isPublished = shift && (shift.isPublished || shift.sharedChanges?.isPublished);
        return isPublished
            ?
                <ShiftRequestsIndicator
                    groupedShiftRequest={ groupedShiftRequest }
                    onIndicatorClick={ this.onShiftRequestIndicatorClick }
                    cellSize={ scheduleCellRenderSize }/>
            :
                null;
    }

    /**
     * Helper function to get the associated with this cell
     */
    private getGroupedShiftRequestForThisCell(): IGroupedOpenShiftRequestEntity {
        const { getGroupedShiftRequestForShiftId, shift } = this.props;
        return getGroupedShiftRequestForShiftId && shift && getGroupedShiftRequestForShiftId(shift.id, ShiftRequestStates.WaitingOnManager);
    }

    /**
     * Callback fired upon click of the shift request indicator. Opens the open shift request approval callout.
     * @param isClick
     */
    private onShiftRequestIndicatorClick = (isClick: boolean) => {
        // trigger request conflict calculations before opening the callout
        const groupedRequest: IGroupedOpenShiftRequestEntity = this.getGroupedShiftRequestForThisCell();
        calculateRequestConflicts(groupedRequest, true /* isOpenShiftRequest */);
        this.showOpenShiftRequestApprovalCallout();
        if (this.props.instrumentScheduleEvent) {
            this.props.instrumentScheduleEvent(InstrumentationService.events.Requests, [
                getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.RequestsEntryPointClicked),
                getGenericEventPropertiesObject(InstrumentationService.properties.Location, InstrumentationService.values.Flyout),
                getGenericEventPropertiesObject(InstrumentationService.properties.RequestType, InstrumentationService.ShiftRequestTypes.AdminOpenShift),
                getGenericEventPropertiesObject(InstrumentationService.properties.EntryPoint, isClick ? InstrumentationService.actionTypes.Mouse : InstrumentationService.actionTypes.EnterKey)
            ]);
        }
    }

    /**
     * Sets the visibility of the open shift request approval callout
     * @param isVisible
     */
    private setIsOpenShiftRequestCalloutVisible(isVisible: boolean) {
        this.setState({ isOpenShiftRequestCalloutVisible: isVisible });
    }

    /**
     * Displays the open shift request approval callout. Hides the other callouts.
     */
    private showOpenShiftRequestApprovalCallout() {
        this.setIsOpenShiftRequestCalloutVisible(true);
        this.hideMoreMenu();
    }

    /**
     * Hides the open shift request approval callout.
     */
    private hideOpenShiftRequestApprovalCallout = () => {
        this.setIsOpenShiftRequestCalloutVisible(false);
    }

    /**
     * Returns true if this component should render an emty cell, as opposed to a regular schedule cell with
     * a normal shift object inside. This is the case if we don't have a shift to render, or if the shift
     * is a deleted shift with active shared changes
     */
    private doRenderEmptyCell(): boolean {
        return !this.props.shift || ShiftUtils.isUnsharedDeletedShift(this.props.shift);
    }

    private isAnyCalloutOpen = (): boolean => {
        return this.state.isMoreMenuCalloutVisible
            || this.state.isOpenShiftRequestCalloutVisible
            || this.state.isShiftLookupCalloutVisible
            || this.state.isConflictCalloutVisible;
    }

    /**
     * Drag source handler — React's synthetic dragstart, fires in bubble phase.
     * All drag setup (ghost, dragState, isDragging) is done in the native capture-phase
     * listener attached in componentDidMount, which runs before any child stopPropagation()
     * can block this handler. This is therefore a no-op in the normal path; it only runs
     * as a fallback if the capture handler somehow didn't set drag state.
     */
    private onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        // If the drag was initiated from the padding area around the shift card, the native
        // capture handler already called e.preventDefault() to cancel the drag.
        // The synthetic event still fires despite the cancellation, so we must guard here too
        // to prevent isDragging from being set (which would permanently fade the card).
        if (this._paddingMouseDown) {
            this._paddingMouseDown = false;
            e.preventDefault();
            return;
        }
        const dragState = getDragState();
        if (dragState.shift) {
            // Capture handler already ran — normal path.
            return;
        }
        // Fallback: capture handler didn't run or shift was null. Set up drag state now.
        const { shift, member, tagId } = this.props;
        if (!shift) { return; }
        setDragState(shift, member, tagId);
        this.setState({ isDragging: true });
    }

    /**
     * Fired when the drag operation ends (drop or cancel).
     * dropEffect === "none" means the user released without a valid drop (cancelled).
     */
    private onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        if (e.dataTransfer.dropEffect === "none") {
            const { shift } = this.props;
            const isTimeOff = shift && ShiftUtils.isTimeOffEvent(shift);
            // For time-off, only log cancellation if the drag reached a valid target at least once.
            // If it never hovered a valid target (e.g. dragged to a different member's row),
            // neither ShiftDragStarted nor ShiftDragCancelled should be instrumented.
            if (!isTimeOff || getDragState().hadValidHover) {
                this.props.instrumentScheduleEvent(InstrumentationService.events.DragAndDrop_ShiftDragCancelled, []);
            }
        }
        clearMultiDayDropPreview();
        clearDragState();
        this.setState({ isDragging: false, isDragOver: false });
    }

    /**
     * Returns true if the current drag payload can be dropped on this cell.
     * Time off shifts may only be dropped on cells belonging to the same member.
     */
    private isDropValid(): boolean {
        const dragState = getDragState();
        if (!dragState.shift) {
            return false;
        }
        if (ShiftUtils.isTimeOffEvent(dragState.shift) && dragState.member?.id !== this.props.member?.id) {
            return false;
        }
        // Block drop onto a target open shift whose slot-count update is still in-flight.
        const { shift } = this.props;
        if (shift && ShiftUtils.isOpenShift(shift) && isTargetOpenShiftUpdatePending(shift.id)) {
            return false;
        }
        return true;
    }

    /**
     * Fired continuously while a dragged item hovers over this cell.
     * preventDefault() is required to signal the cell accepts drops.
     */
    private onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (this.isDropValid()) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            const cellRect = e.currentTarget.getBoundingClientRect();
            this.updateMultiDayDropPreview(e.clientX, cellRect);
            // A multi-day time off can move to a different day within its own source cell
            // without ever crossing a cell boundary (so onDragEnter never fires for it).
            // Evaluate the deferred drag-started telemetry here too so that path still emits
            // a ShiftDragStarted to pair with its ShiftDropCompleted.
            this.maybeInstrumentDeferredDragStart(e.clientX, cellRect);
        }
    }

    /**
     * Fired when a dragged item first enters this cell's bounds.
     */
    private onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        if (this.isDropValid()) {
            e.preventDefault();
            const cellRect = e.currentTarget.getBoundingClientRect();
            this.updateMultiDayDropPreview(e.clientX, cellRect);
            // Show the drop indicator on every valid target, including the source cell on
            // re-entry, so the user always gets hover feedback about where the drop will land.
            this.setState({ isDragOver: true });
            this.maybeInstrumentDeferredDragStart(e.clientX, cellRect);
        }
    }

    /**
     * Determines whether the current hover over this cell should count as a "valid hover"
     * over a real target for drag telemetry.
     *
     * A hover over a non-source cell is always valid. A hover over the source cell is only
     * valid when a multi-day drop preview would actually move the time off to a different
     * day than its real start — true same-day re-entry stays a no-op.
     */
    private isValidTelemetryHover(clientX: number, cellRect: DOMRect): boolean {
        const { shift: draggedShift } = getDragState();
        const isSourceCell = draggedShift?.id === this.props.shift?.id;
        if (!isSourceCell) {
            return true;
        }
        const preview = this.getMultiDayDropPreview(clientX, cellRect);
        return !!(preview && draggedShift && !preview.targetStartTime.isSame(draggedShift.startTime, "day"));
    }

    /**
     * Fires the deferred ShiftDragStarted telemetry for a time-off drag on its first valid
     * hover over a real target. Idempotent for the lifetime of a drag: hadValidHover gates it
     * so repeated onDragOver / onDragEnter calls emit the event at most once.
     */
    private maybeInstrumentDeferredDragStart(clientX: number, cellRect: DOMRect): void {
        const dragState = getDragState();
        if (dragState.hadValidHover || !this.isValidTelemetryHover(clientX, cellRect)) {
            return;
        }
        setHadValidHover();
        if (dragState.shift && ShiftUtils.isTimeOffEvent(dragState.shift)) {
            this.props.instrumentScheduleEvent(InstrumentationService.events.DragAndDrop_ShiftDragStarted, [
                getGenericEventPropertiesObject(InstrumentationService.properties.CellType, InstrumentationUtils.getInstrumentationCellType(dragState.shift))
            ]);
        }
    }

    /**
     * Fired when a dragged item leaves this cell's bounds.
     * relatedTarget check prevents flickering when the cursor moves over a child
     * element inside this cell — those child dragleave events bubble up, but
     * relatedTarget will still be inside the cell, so we ignore them.
     */
    private onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        const leavingCell = !e.currentTarget.contains(e.relatedTarget as Node);
        if (leavingCell) {
            clearMultiDayDropPreview(this.getDropPreviewOwnerId());
            this.setState({ isDragOver: false });
        }
    }

    private getDropPreviewOwnerId(): string {
        return this._dropPreviewOwnerId;
    }

    private isMultiDayTimeOffDrag(): boolean {
        const { shift } = getDragState();
        if (!shift ||
            !ShiftUtils.isTimeOffEvent(shift) ||
            this.props.scheduleCalendarType === ScheduleCalendarTypes.Day) {
            return false;
        }

        return ScheduleGridUtils.fastGetCellUnitSizeForMultiDayViews(
            ScheduleGridUtils.roundShiftTemporalItemTimeByDay(shift.startTime, true),
            ScheduleGridUtils.roundShiftTemporalItemTimeByDay(shift.endTime, false)) > 1;
    }

    /**
     * Calculates the full-width preview for a multi-day time-off drop. A target may
     * itself span multiple days, so the pointer determines which day is the anchor.
     */
    private getMultiDayDropPreview(clientX: number, cellRect: DOMRect): MultiDayDropPreview | null {
        const { shift: draggedShift } = getDragState();
        const { cellStartTime, cellEndTime, viewEnd } = this.props;
        if (!this.isMultiDayTimeOffDrag() ||
            !draggedShift ||
            !cellStartTime ||
            !cellEndTime ||
            cellRect.width <= 0) {
            return null;
        }

        const draggedDayCount = ScheduleGridUtils.fastGetCellUnitSizeForMultiDayViews(
            ScheduleGridUtils.roundShiftTemporalItemTimeByDay(draggedShift.startTime, true),
            ScheduleGridUtils.roundShiftTemporalItemTimeByDay(draggedShift.endTime, false));
        const targetCellDayCount = Math.max(1,
            ScheduleGridUtils.fastGetCellUnitSizeForMultiDayViews(cellStartTime, cellEndTime));
        const dayWidth = cellRect.width / targetCellDayCount;
        const pointerOffset = Math.max(0, Math.min(cellRect.width - 1, clientX - cellRect.left));
        const targetDayIndex = Math.min(targetCellDayCount - 1, Math.floor(pointerOffset / dayWidth));
        const targetStartTime = cellStartTime.clone().add(targetDayIndex, "days");
        const visibleDayCount = viewEnd
            ? Math.max(1, ScheduleGridUtils.fastGetCellUnitSizeForMultiDayViews(targetStartTime, viewEnd))
            : draggedDayCount;

        return {
            targetStartTime,
            left: cellRect.left + targetDayIndex * dayWidth,
            width: dayWidth * Math.min(draggedDayCount, visibleDayCount)
        };
    }

    private updateMultiDayDropPreview(clientX: number, cellRect: DOMRect): void {
        const preview = this.getMultiDayDropPreview(clientX, cellRect);
        if (preview) {
            showMultiDayDropPreview(this.getDropPreviewOwnerId(), preview, cellRect);
        } else {
            clearMultiDayDropPreview(this.getDropPreviewOwnerId());
        }
    }

    /**
     * Maps a horizontal pointer position to the portion of a shift rendered in this cell.
     * Overnight shifts are clipped at the day boundary, so using the full shift duration
     * would place the pointer several hours away from its visible position.
     */
    private getDayViewCursorTime(clientX: number, cellRect: DOMRect): Moment | null {
        const { shift, cellStartTime, cellEndTime } = this.props;
        if (!shift || cellRect.width <= 0) {
            return null;
        }

        const renderedStartTime = cellStartTime || shift.startTime;
        const renderedEndTime = cellEndTime || shift.endTime;
        const renderedDurationMinutes = renderedEndTime.diff(renderedStartTime, "minutes", true);
        if (renderedDurationMinutes <= 0) {
            return null;
        }

        const fraction = Math.max(0, Math.min(1, (clientX - cellRect.left) / cellRect.width));
        return renderedStartTime.clone().add(fraction * renderedDurationMinutes, "minutes");
    }

    /**
     * Fired when a dragged shift is released onto this cell.
     * Delegates to the onShiftDrop closure (supplied by ScheduleGridUtils)
     * which already captures the target cell's member, tagId, startTime,
     * and existing shift.
     */
    private onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        clearMultiDayDropPreview();
        this.setState({ isDragOver: false });
        if (getDragState().shift) {
            // In day view, dropping on the source shift cell uses the pre-captured startTime
            // (the shift's current position), so the shift never moves. Compute a cursor-derived
            // target time instead, allowing small positional adjustments within the source range.
            const { shift: draggedShift } = getDragState();
            const { shift: cellShift, scheduleCalendarType } = this.props;
            const cellRect = e.currentTarget.getBoundingClientRect();
            if (scheduleCalendarType === ScheduleCalendarTypes.Day &&
                cellShift && draggedShift && cellShift.id === draggedShift.id) {
                const cursorTime = this.getDayViewCursorTime(e.clientX, cellRect);
                if (cursorTime) {
                    // Subtract the original grab offset so the shift ends up at the position
                    // represented by the ghost image, including when only part of it is visible.
                    const rawTarget = cursorTime.subtract(getDayViewGrabOffsetMinutes(), "minutes");
                    // Snap to 30-min boundary.
                    const snapped = Math.round(rawTarget.minute() / 30) * 30;
                    rawTarget.minute(snapped).second(0).millisecond(0);
                    setOverrideDropStartTime(rawTarget);
                }
            } else {
                const preview = this.getMultiDayDropPreview(e.clientX, cellRect);
                // Always forward the anchored preview day so the committed date matches what the
                // preview showed. The target cell's own cellStartTime may be a different day than
                // the hovered sub-day — e.g. a multi-day target where the user hovered a later day,
                // or a first-visible day clamped to viewStart — and PeopleViewGrid would otherwise
                // fall back to that cellStartTime and silently move the shift to the wrong day.
                // Suppress the override only for a genuine source-cell same-day no-op (the shift
                // dropped back on its own start day). PeopleViewGrid still decides whether an
                // override is a real move and only clears a true self-target.
                const isSourceCell = !!cellShift && cellShift.id === draggedShift.id;
                const isSameDayAsSource = !!preview && preview.targetStartTime.isSame(draggedShift.startTime, "day");
                if (preview && !(isSourceCell && isSameDayAsSource)) {
                    setOverrideDropStartTime(preview.targetStartTime);
                }
            }
            this.props.onShiftDrop?.();
        }
    }

    /**
     * Callback fire onMouseEnter event. Displays the tooltip
     */
     private onMouseEnter = () => {
        this.setState({ renderExtras: true });
    }

    /**
     * Callback fire onMouseExit event. Hides the tooltip
     */
    private onMouseLeave = () => {
        if (!this.isAnyCalloutOpen()) {
            this.setState({ renderExtras: false });
        }
    }

    render() {
        const { inOpenShiftRow, shift, showShiftRequestsIndicator, scheduleCalendarType, isDragEnabled, onShiftDrop } = this.props;
        const { isDragOver, isDragging } = this.state;

        if (!ScheduleCell._emptyCellContainerClasses) {
            // _emptyCellContainerClasses is cleared in the ctor when the calendar type changes
            ScheduleCell._emptyCellContainerClasses = classNames(SCHEDULE_CELL_BASE_CSSCLASS,
                styles.emptyContent,
                {[styles.hideBorder]: scheduleCalendarType === ScheduleCalendarTypes.Day});
        }
        if (!ScheduleCell._emptyCellOpenShiftContainerClasses) {
            // _emptyCellContainerClasses is cleared in the ctor when the calendar type changes
            ScheduleCell._emptyCellOpenShiftContainerClasses = classNames(SCHEDULE_CELL_BASE_CSSCLASS,
                styles.emptyContent,
                styles.openCell,
                {[styles.hideBorder]: scheduleCalendarType === ScheduleCalendarTypes.Day});
        }

        const renderEmptyCell = this.doRenderEmptyCell();
        const isDnDWhitespace = this.props.isAdmin && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableShiftDragAndDrop);
        // Any non-empty drag-enabled cell is a potential drag source — no prior selection required.
        // MarqueeSelection is prevented from starting on these cells via data-shift-dnd-source
        // (see PeopleViewGrid.onShouldStartSelection), so click-to-select still works normally.
        const isDragSource = isDragEnabled && !renderEmptyCell;
        const isDropTarget = !!onShiftDrop && isDnDWhitespace;
        // The requests indicator only renders when the shift is published (renderShiftRequestsIndicator
        // returns null for drafts). Mirror that check here so openShiftWithRequests — which shrinks
        // the card from 62px to 46px — is only set when the indicator is actually visible.
        const isShiftPublished = shift && (shift.isPublished || shift.sharedChanges?.isPublished);

        const baseClass = renderEmptyCell
            ? (inOpenShiftRow ? ScheduleCell._emptyCellOpenShiftContainerClasses : ScheduleCell._emptyCellContainerClasses)
            : ScheduleCell._containerClasses;
        const containerClass = classNames(baseClass, {
            [styles.isDragOver]: isDragOver && !this.isMultiDayTimeOffDrag(),
            [styles.isDragging]: isDragging,
            // Placed directly on the cell container (parent of ShiftThemeContainer) so the CSS selector
            // .dndOpenShiftCell .shiftContainer is a reliable single-level ancestor match.
            // Used by ShiftThemeContainer.scss to set card height (62px no-requests / 46px with-requests).
            "dndOpenShiftCell": isDnDWhitespace && inOpenShiftRow && !renderEmptyCell,
            // Used by ShiftThemeContainer.scss and ShiftRequestsIndicator.scss to shrink the card and
            // reposition the requests indicator when the request count is visible.
            // Only applied when the indicator actually renders (published shift) — draft shifts with
            // 0 requests render null from renderShiftRequestsIndicator and must keep the 62px height.
            "openShiftWithRequests": isDnDWhitespace && inOpenShiftRow && showShiftRequestsIndicator && isShiftPublished && !renderEmptyCell
        });

        return (
            <div className={ containerClass }
                draggable={ isDragSource }
                data-shift-dnd-source={ isDragSource ? "true" : undefined }
                data-empty-shift-cell={ (renderEmptyCell && isDropTarget) ? "true" : undefined }
                data-non-drag-shift-cell={ (isDnDWhitespace && !isDragSource && !renderEmptyCell) ? "true" : undefined }
                data-empty-cell={ renderEmptyCell ? "true" : undefined }
                onMouseDown={ isDragSource ? (e: React.MouseEvent<HTMLDivElement>) => {
                    // Use coordinate-based hit detection to distinguish padding area clicks
                    // from shift card clicks. This is more reliable than e.target === e.currentTarget
                    // because some child elements may cover the padding visually.
                    if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableShiftDragAndDrop)) {
                        const el = e.currentTarget as HTMLElement;
                        const rect = el.getBoundingClientRect();
                        const relX = e.clientX - rect.left;
                        const relY = e.clientY - rect.top;
                        if (relX < DND_PADDING_LEFT_RIGHT || relY < DND_PADDING_TOP || relX > rect.width - DND_PADDING_LEFT_RIGHT || relY > rect.height - DND_PADDING_BOTTOM) {
                            // Padding area — allow propagation so SelectionZone/MarqueeSelection
                            // handles multi-select. Flag subsequent dragstart for cancellation.
                            this._paddingMouseDown = true;
                            // Temporarily remove draggable so the browser never enters drag mode,
                            // which would suppress mousemove events that MarqueeSelection needs.
                            el.draggable = false;
                            document.addEventListener("mouseup", this._onPaddingMouseUp, { once: true });
                            // Additional recovery: restore draggable if the pointer leaves the cell
                            // or is cancelled (e.g. user releases mouse outside the browser window),
                            // preventing the cell from getting stuck as non-draggable.
                            el.addEventListener("mouseleave", this._onPaddingMouseUp, { once: true });
                            el.addEventListener("pointercancel", this._onPaddingMouseUp, { once: true });
                            return;
                        }
                    }
                    // Shift card area (or flag off) — stop propagation so SelectionZone does not
                    // interfere with drag initiation. Reset flag in case a prior padding mousedown
                    // was not followed by a drag.
                    this._paddingMouseDown = false;
                    e.stopPropagation();
                } : undefined }
                onDragStart={ isDragSource ? this.onDragStart : undefined }
                onDragEnd={ (isDragSource || isDragging) ? this.onDragEnd : undefined }
                onDragOver={ isDropTarget ? this.onDragOver : undefined }
                onDragEnter={ isDropTarget ? this.onDragEnter : undefined }
                onDragLeave={ isDropTarget ? this.onDragLeave : undefined }
                onDrop={ isDropTarget ? this.onDrop : undefined }
                onContextMenu={ this.onRightClick }
                onClick={ this.onLeftClick }
                onMouseEnter={ this.onMouseEnter }
                onMouseLeave={ this.onMouseLeave }
                ref={ this._cellElement }>
                {
                    !renderEmptyCell &&
                        <PrintComponent>
                            <PrintBackground shift={ shift } />
                        </PrintComponent>
                }
                { renderEmptyCell ? this.renderEmptyCell() : this.renderShift() }
                {
                    shift && showShiftRequestsIndicator && !renderEmptyCell &&
                        this.renderShiftRequestsIndicator()
                }
                { this.renderCallout() }
            </div>
        );
    }
}

export default ScheduleCell;